import React, { useState } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import CategoryBarChart from '../components/charts/instances/CategoryBarChart';
import CumulativeStackedBarChart from '../components/charts/instances/CumulativeStackedBarChart';
import BurnRateLineChart from '../components/charts/instances/BurnRateLineChart';
import AmountRangeBarChart from '../components/charts/instances/AmountRangeBarChart';
import P2PvsCommercePieChart from '../components/charts/instances/P2PvsCommercePieChart';
import CreditDebitBarChart from '../components/charts/instances/CreditDebitBarChart';

const CATEGORIES_QUERY = gql`
  query CategoriesForDashboard {
    categories {
      id
      name
      color
    }
  }
`;

const UNCATEGORIZED_COUNT_QUERY = gql`
  query UncategorizedCountForDashboard {
    transactions(hasCategory: false, limit: 0) {
      totalCount
    }
  }
`;

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState(() => dayjs());
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(null);

  const { data: categoriesData } = useQuery(CATEGORIES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: uncategorizedData } = useQuery(UNCATEGORIZED_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const categories = categoriesData?.categories || [];
  const uncategorizedCount = uncategorizedData?.transactions?.totalCount || 0;

  const handleCategoryChange = (event) => {
    const ids = event.target.value;
    // If every category is selected, store null (no filter sent to backend)
    if (ids.length === categories.length || ids.length === 0) {
      setSelectedCategoryIds(null);
    } else {
      setSelectedCategoryIds(ids);
    }
  };

  const dateProps = { dateFrom, dateTo, categoryIds: selectedCategoryIds };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Visualización de tus movimientos financieros
      </Typography>

      {/* Uncategorized banner */}
      {uncategorizedCount > 0 && (
        <Alert
          severity="info"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/classify')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/classify');
            }
          }}
          sx={{
            mb: 3,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: 'secondary.main',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            boxShadow: `0 0 12px 2px ${theme.palette.secondary.main}`,
            '@keyframes glow': {
              '0%, 100%': {
                boxShadow: `0 0 12px 2px ${theme.palette.secondary.main}`,
              },
              '50%': {
                boxShadow: `0 0 20px 5px ${theme.palette.secondary.light}`,
              },
            },
            animation: 'glow 2s ease-in-out infinite',
            '& .MuiAlert-icon': {
              color: 'secondary.contrastText',
            },
          }}
        >
          Tienes {uncategorizedCount} transacción
          {uncategorizedCount !== 1 ? 'es' : ''} sin categorizar — haz clic para clasificarlas
        </Alert>
      )}

      {/* Filter bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderLeft: '3px solid',
          borderLeftColor: 'secondary.main',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
        >
          <DatePicker
            label="Desde"
            value={dateFrom}
            onChange={setDateFrom}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Hasta"
            value={dateTo}
            onChange={setDateTo}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small' } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 240 } }}>
            <InputLabel id="dashboard-category-label">Categorías</InputLabel>
            <Select
              labelId="dashboard-category-label"
              id="dashboard-category-select"
              multiple
              value={selectedCategoryIds ?? categories.map((c) => parseInt(c.id))}
              label="Categorías"
              onChange={handleCategoryChange}
              renderValue={(selected) => {
                if (selectedCategoryIds === null) return 'Todas';
                const n = selected.length;
                if (n === 1) {
                  const cat = categories.find(
                    (c) => parseInt(c.id) === parseInt(selected[0])
                  );
                  return cat ? cat.name : '1 categoría';
                }
                return `${n} categorías`;
              }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={parseInt(cat.id)}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: cat.color,
                      mr: 1.5,
                      flexShrink: 0,
                      display: 'inline-block',
                    }}
                  />
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDateFrom(dayjs().startOf('month'));
              setDateTo(dayjs());
            }}
          >
            Mes actual
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDateFrom(dayjs().subtract(3, 'month').startOf('month'));
              setDateTo(dayjs());
            }}
          >
            Últimos 3 meses
          </Button>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => {
              setDateFrom(null);
              setDateTo(null);
            }}
            sx={{ color: 'text.secondary' }}
          >
            Todo el tiempo
          </Button>
        </Stack>
      </Paper>

      {/* Charts grid */}
      <Grid container spacing={3}>
        {/* Row 1: Category spend — full width, taller */}
        <Grid item xs={12}>
          <CategoryBarChart {...dateProps} />
        </Grid>

        {/* Row 2: Cumulative stacked + Burn rate line */}
        <Grid item xs={12} md={6}>
          <CumulativeStackedBarChart {...dateProps} />
        </Grid>
        <Grid item xs={12} md={6}>
          <BurnRateLineChart {...dateProps} />
        </Grid>

        {/* Row 3: Credit/Debit monthly + P2P vs Commerce */}
        <Grid item xs={12} md={6}>
          <CreditDebitBarChart {...dateProps} />
        </Grid>
        <Grid item xs={12} md={6}>
          <P2PvsCommercePieChart {...dateProps} />
        </Grid>

        {/* Row 4: Amount range distribution */}
        <Grid item xs={12} md={6}>
          <AmountRangeBarChart {...dateProps} />
        </Grid>

        {/* Spacer to balance the row */}
        <Grid item xs={12} md={6}>
          <Box />
        </Grid>
      </Grid>
    </Container>
  );
}
